import { toSafeJsonLdString } from "@/utils/safeJsonLd";

import { blogIndexHref, type BlogIndexView } from "./indexView";
import { BLOG_PILLAR_LABELS } from "./pillars";
import type { BlogLanguage, BlogPost, BlogPostMeta } from "./types";

export const SITE_URL = "https://diningmate.com.au";
const SITE_NAME = "DiningMate";
const DEFAULT_OG_IMAGE = { url: `${SITE_URL}/og-image.png`, width: "1200", height: "630" };
const PUBLISHER_LOGO = `${SITE_URL}/icon-512.png`;

const OG_LOCALES: Record<BlogLanguage, string> = { en: "en_AU", ko: "ko_KR", zh: "zh_CN" };

type Meta = Record<string, string>;
type Head = {
    meta: Meta[];
    links: Record<string, string>[];
    scripts: { type: string; children: string }[];
};

const absolute = (path: string): string => (path.startsWith("http") ? path : `${SITE_URL}${path}`);

const jsonLd = (schema: unknown): Head["scripts"][number] => ({
    type: "application/ld+json",
    children: toSafeJsonLdString(schema),
});

const organization = {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: PUBLISHER_LOGO },
};

const socialMeta = (
    title: string,
    description: string,
    image: { url: string; width?: string; height?: string; alt?: string },
): Meta[] => [
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: image.url },
    ...(image.width ? [{ property: "og:image:width", content: image.width }] : []),
    ...(image.height ? [{ property: "og:image:height", content: image.height }] : []),
    ...(image.alt ? [{ property: "og:image:alt", content: image.alt }] : []),
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image.url },
    ...(image.alt ? [{ name: "twitter:image:alt", content: image.alt }] : []),
];

export const buildPostHead = (post: BlogPost): Head => {
    const url = `${SITE_URL}/blog/${post.slug}`;
    const title = `${post.title} | ${SITE_NAME} Blog`;
    const image = post.image ? { url: absolute(post.image), alt: post.imageAlt } : DEFAULT_OG_IMAGE;
    const modifiedAt = post.updatedAt ?? post.publishedAt;

    const blogPosting = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.description,
        datePublished: post.publishedAt,
        dateModified: modifiedAt,
        inLanguage: post.language,
        image: [image.url],
        articleSection: BLOG_PILLAR_LABELS[post.pillar],
        // Word counts are only meaningful for space-delimited languages.
        ...(post.language === "en" && {
            wordCount: post.content.split(/\s+/).filter(Boolean).length,
        }),
        ...(post.tags && { keywords: post.tags.join(", ") }),
        author: organization,
        publisher: organization,
        mainEntityOfPage: url,
    };

    const breadcrumbs = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
    };

    return {
        meta: [
            { title },
            { name: "description", content: post.description },
            { property: "og:type", content: "article" },
            { property: "og:url", content: url },
            { property: "og:locale", content: OG_LOCALES[post.language] },
            ...socialMeta(title, post.description, image),
            { property: "article:published_time", content: post.publishedAt },
            { property: "article:modified_time", content: modifiedAt },
            { property: "article:section", content: BLOG_PILLAR_LABELS[post.pillar] },
            ...(post.tags ?? []).map((tag) => ({ property: "article:tag", content: tag })),
        ],
        links: [{ rel: "canonical", href: url }],
        scripts: [jsonLd(blogPosting), jsonLd(breadcrumbs)],
    };
};

const INDEX_TITLE = `Blog | ${SITE_NAME}`;
const INDEX_DESCRIPTION =
    "Guides and comparisons on restaurant bookings, no-shows, and running front-of-house for Australian venues.";

export const buildIndexHead = (
    view: Pick<BlogIndexView, "page" | "totalPages" | "pillar">,
    posts: BlogPostMeta[],
): Head => {
    const { page, totalPages, pillar } = view;
    const title = page > 1 ? `Blog – Page ${page} | ${SITE_NAME}` : INDEX_TITLE;

    // A pillar filter is a re-slice of the same posts, so it points back at /blog instead of
    // competing with it. Pages of the unfiltered list are distinct content and canonicalise to
    // themselves, linked together with prev/next.
    const canonicalPath = pillar ? "/blog" : blogIndexHref({ page });
    const prev = !pillar && page > 1 ? blogIndexHref({ page: page - 1 }) : undefined;
    const next = !pillar && page < totalPages ? blogIndexHref({ page: page + 1 }) : undefined;

    const blog = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: INDEX_TITLE,
        description: INDEX_DESCRIPTION,
        url: `${SITE_URL}/blog`,
        publisher: organization,
        blogPost: posts.map((post) => ({
            "@type": "BlogPosting",
            headline: post.title,
            url: `${SITE_URL}/blog/${post.slug}`,
            datePublished: post.publishedAt,
            ...(post.image && { image: absolute(post.image) }),
        })),
    };

    return {
        meta: [
            { title },
            { name: "description", content: INDEX_DESCRIPTION },
            { property: "og:type", content: "website" },
            { property: "og:url", content: `${SITE_URL}${canonicalPath}` },
            ...socialMeta(title, INDEX_DESCRIPTION, DEFAULT_OG_IMAGE),
        ],
        links: [
            { rel: "canonical", href: `${SITE_URL}${canonicalPath}` },
            ...(prev ? [{ rel: "prev", href: `${SITE_URL}${prev}` }] : []),
            ...(next ? [{ rel: "next", href: `${SITE_URL}${next}` }] : []),
        ],
        scripts: [jsonLd(blog)],
    };
};
